import { useEffect, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";

export default function VersionInfoModal({ isOpen, onClose }) {
  const [releaseInfo, setReleaseInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    setError(null);

    fetch("/api/release")
      .then(async (res) => {
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`API ${res.status}: ${txt}`);
        }
        return res.json();
      })
      .then((data) => {
        setReleaseInfo(data || null);
      })
      .catch((err) => {
        console.error("Error fetching release:", err);
        setError(err.message || "Unknown error");
        setReleaseInfo(null);
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  const handleOverlayClick = () => onClose();


  const heuristicReorder = (markdown = "") => {
    if (!markdown || typeof markdown !== "string") return "";

    const sections = markdown.split(/(?=^#{1,3}\s)/m);
    const features = [];
    const fixes = [];
    const others = [];

    for (const s of sections) {
      const headingMatch = s.match(/^#{1,3}\s*(.+)/m);
      const title = headingMatch ? headingMatch[1].toLowerCase() : "";

      if (/feature|add|enhanc|new/i.test(title) || /feature|added|enhanc/i.test(s)) {
        features.push(s);
      } else if (/fix|bug|patch|hotfix/i.test(title) || /fix(ed|es)?|bug(s)?/i.test(s)) {
        fixes.push(s);
      } else {
        if (!headingMatch && /^(?:\s*[-*+]\s)/m.test(s)) {

          const lines = s.split("\n");
          const featureLines = [];
          const fixLines = [];
          const restLines = [];
          for (const L of lines) {
            if (/fix|bug|patch|hotfix/i.test(L)) fixLines.push(L);
            else if (/feature|add|enhanc|new/i.test(L)) featureLines.push(L);
            else restLines.push(L);
          }
          if (featureLines.length && !fixLines.length) features.push(s);
          else if (fixLines.length && !featureLines.length) fixes.push(s);
          else others.push(s);
        } else {
          others.push(s);
        }
      }
    }

    const ordered = [...features, ...others, ...fixes].filter(Boolean);

    if (ordered.length === 0) return markdown;
    return ordered.join("\n\n");
  };

  const notesToRender = useMemo(() => {
    if (!releaseInfo) return "";
    if (releaseInfo.notesReordered) return releaseInfo.notesReordered;
    return heuristicReorder(releaseInfo.notes || "");
  }, [releaseInfo]);

  const publishedDate = (() => {
    if (!releaseInfo?.publishedAt) return "Unknown";
    const d = new Date(releaseInfo.publishedAt);
    return isNaN(d.getTime()) ? "Unknown" : d.toLocaleDateString();
  })();


  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true">
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal">
          <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2>Version Info</h2>
          </header>

          {loading && <p>Loading release info...</p>}
          {error && <p style={{ color: "red" }}>Error: {error}</p>}

          {!loading && !error && releaseInfo && (
            <>
              <p><strong>Version:</strong> {releaseInfo.version ?? releaseInfo.tag_name ?? "Unknown"}</p>
              <p><strong>Released:</strong> {publishedDate}</p>

              <div style={{ whiteSpace: "pre-wrap", marginTop: 12 }}>
                {notesToRender ? (
                  <ReactMarkdown>{notesToRender}</ReactMarkdown>
                ) : (
                  <p><em>No release notes provided.</em></p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
